import React, { useContext, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import CloudBasedStudyStore from 'app/adapters/CloudBasedStudyStore';
import CombinedStudyStore from 'app/adapters/CombinedStudyStore';
import CornerstoneImageLoader from 'app/adapters/CornerstoneImageLoader';
import LocalStudyStore from 'app/adapters/LocalStudyStore';
import IDisplayableImageEncoderContext from 'app/providers/IDisplayableImageEncoder/context';
import { LocalDicomStoreContext } from 'app/providers/LocalDicomStoreProvider';
import StudyStoreContext from 'app/providers/StudyStoreProvider/context';
import { isElectron } from 'app/utils/envUtil';
import PACSStudyStoreAdataper from 'app/adapters/PACSStudyStoreAdapter';
import { PACSCommunicationContext } from 'app/components/PACSCommunicationProvider/context';
import { useSelector } from 'react-redux';
import { selectClinicInfo } from 'app/redux/clinicInfo/reducer';

export default function CombinedStudyStoreProvider({ children }: React.PropsWithChildren<{}>) {
  const intl = useIntl();
  const localDicomStore = useContext(LocalDicomStoreContext);
  const pacsCommunication = useContext(PACSCommunicationContext);

  const [imageLoader] = useState(() => new CornerstoneImageLoader());
  const [pacsStudyStoreAdapter] = useState(
    () => new PACSStudyStoreAdataper(pacsCommunication, imageLoader)
  );
  const imageEncoder = useContext(IDisplayableImageEncoderContext);
  const [localStudyStore] = useState(() => (isElectron() ? new LocalStudyStore() : undefined));
  const [cloudBaseStudyStore] = useState(() => new CloudBasedStudyStore(imageLoader, imageEncoder));
  const [studyStore] = useState(() =>
    localStudyStore
      ? new CombinedStudyStore(localStudyStore, cloudBaseStudyStore, pacsStudyStoreAdapter)
      : cloudBaseStudyStore
  );
  const clinicInfo = useSelector(selectClinicInfo);

  useEffect(() => {
    localStudyStore?.syncIntl(intl);
  }, [intl]);

  useEffect(() => {
    localStudyStore?.syncLocalDicomStore(localDicomStore);
  }, [localDicomStore]);

  useEffect(() => {
    pacsStudyStoreAdapter.pacsCommunication = pacsCommunication;
    pacsStudyStoreAdapter.imageLoader = imageLoader;
  }, [pacsCommunication, imageLoader]);

  useEffect(() => {
    localStudyStore?.syncClinicInfo(clinicInfo);
  }, [clinicInfo]);

  return <StudyStoreContext.Provider value={studyStore}>{children}</StudyStoreContext.Provider>;
}
